import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { environment } from '~env';
import { ClearPaymentState, ProceedCustomPayment, SearchBill } from './store/payment.actions';
import { BillLine, BillSearchResult, PaymentBillSource, PaymentReceipt } from './store/payment.model';
import { PaymentQuery } from './store/payment.query';

@Component({
  selector: 'app-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PaymentComponent implements OnInit, OnDestroy {
  selectedIndex = 0;
  invoiceForm: FormGroup;
  private destroy$ = new Subject<void>();

  loading$ = this.paymentQuery.loading$;
  result$ = this.paymentQuery.result$;
  error$ = this.paymentQuery.error$;
  paymentReceipt$ = this.paymentQuery.paymentReceipt$;
  paymentError$ = this.paymentQuery.paymentError$;

  isLoading = false;
  paymentError: string | null = null;
  nameUnderCard = '';
  invoiceVerified = false;
  chargeAuthorized = false;
  storeId = '';
  paymentSuccess = false;
  paymentReceipt: PaymentReceipt | null = null;
  hidePayment = false;
  canRetry = false;
  showPaymentForm = false;
  isMobile = false;
  isDesktop = true;

  get isTablet(): boolean {
    return !this.isDesktop && !this.isMobile;
  }

  get canSubmitPayment(): boolean {
    return !!this.nameUnderCard?.trim() && this.invoiceVerified && this.chargeAuthorized;
  }

  private get isEnglish(): boolean {
    return this.translate.currentLang === 'en';
  }

  get dateLocale(): string {
    return this.isEnglish ? 'en-US' : 'fr';
  }

  formatAmount(amount: number): string {
    return new Intl.NumberFormat(this.isEnglish ? 'en-CA' : 'fr-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount ?? 0);
  }

  getSubtotal(result: BillSearchResult): number {
    const subtotal = (result.amount ?? 0) - (result.tpsAmount ?? 0) - (result.tvqAmount ?? 0);
    return Math.round(subtotal * 100) / 100;
  }

  getLineDescription(line: BillLine): string {
    const description = line.description;
    return (this.isEnglish ? description?.en || description?.fr : description?.fr || description?.en) || 'N/A';
  }

  getInvoiceDescription(result: BillSearchResult): string {
    return (result.billLines ?? []).map((line) => this.getLineDescription(line)).join(', ') || 'N/A';
  }

  formatTransactionDate(value: string): string {
    if (!value) {
      return '';
    }
    const date = new Date(/^\d{4}-\d{2}-\d{2} \d/.test(value) ? value.replace(' ', 'T') : value);
    if (isNaN(date.getTime())) {
      return value;
    }
    return new Intl.DateTimeFormat(this.isEnglish ? 'en-CA' : 'fr-CA', {
      dateStyle: 'long',
      timeStyle: 'short',
    }).format(date);
  }

  /** Moneris returns card type codes (V, M, AX, ...) */
  getCardTypeLabel(cardType: string): string {
    const labels: Record<string, string> = {
      V: 'Visa',
      M: 'Mastercard',
      AX: 'American Express',
      DC: 'Diners Club',
      NO: 'Discover',
      C1: 'JCB',
    };
    return labels[cardType?.trim().toUpperCase()] ?? cardType;
  }

  goHome(): void {
    this.router.navigate(['/home']);
  }

  printReceipt(): void {
    window.print();
  }

  constructor(
    private fb: FormBuilder,
    private store: Store,
    public paymentQuery: PaymentQuery,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private breakpointObserver: BreakpointObserver,
    private router: Router
  ) {
    this.invoiceForm = this.fb.group({
      billNumber: ['', Validators.required],
      payerName: ['', Validators.required],
      amount: ['', [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
    });
  }

  private blurActiveElement(): void {
    const el = document.activeElement as HTMLElement;
    if (el && typeof el.blur === 'function') {
      el.blur();
    }
  }

  ngOnInit() {
    const mobileQuery = '(max-width: 767.98px)';
    const desktopQuery = '(min-width: 1200px)';
    this.breakpointObserver
      .observe([mobileQuery, desktopQuery])
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        this.isMobile = state.breakpoints[mobileQuery];
        this.isDesktop = state.breakpoints[desktopQuery];
        this.cdr.markForCheck();
      });

    this.loading$.pipe(takeUntil(this.destroy$)).subscribe((loading) => {
      if (!loading && !this.paymentError && !this.paymentSuccess) {
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });

    this.result$.pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (result) {
        this.storeId = this.resolveMonerisStoreId(result.source);
        this.ngZone.runOutsideAngular(() => {
          setTimeout(() => {
            this.ngZone.run(() => {
              this.blurActiveElement();
              this.setSelectedIndex(1);
              this.cdr.markForCheck();
              setTimeout(() => {
                this.showPaymentForm = true;
                this.cdr.markForCheck();
              }, 500);
            });
          }, 100);
        });
      }
    });

    this.error$.pipe(takeUntil(this.destroy$)).subscribe((error) => {
      if (error) {
        this.isLoading = false;
        this.hidePayment = false;
        this.showPaymentForm = false;
        this.cdr.markForCheck();
      }
    });

    this.paymentReceipt$.pipe(takeUntil(this.destroy$)).subscribe((receipt) => {
      if (receipt) {
        this.isLoading = false;
        this.paymentSuccess = true;
        this.paymentReceipt = receipt;
        this.hidePayment = true;
        this.resetForm();
      }
    });

    this.paymentError$.pipe(takeUntil(this.destroy$)).subscribe((error) => {
      if (error) {
        this.isLoading = false;

        let cleanError = error;
        if (
          typeof error === 'string' &&
          (error.includes('Http failure response for') ||
            error.includes('500 Internal Server Error') ||
            error.includes('Internal Server Error'))
        ) {
          cleanError = this.translate.instant('memberBills.invoicePayment.errors.paymentError');
        }

        this.paymentError = cleanError;
        this.hidePayment = true;
        this.canRetry = cleanError.toLowerCase().indexOf('critique') < 0;
        this.cdr.markForCheck();
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setSelectedIndex(index: number) {
    this.selectedIndex = index;
    if (index === 0) {
      this.resetPaymentState();
      this.showPaymentForm = false;
    }
    this.cdr.markForCheck();
  }

  tabChanged(event: any) {
    this.setSelectedIndex(event.index);
  }

  searchInvoice() {
    if (this.invoiceForm.invalid) return;

    this.setSelectedIndex(0);
    this.resetPaymentState();

    const { billNumber, payerName, amount } = this.invoiceForm.value;
    this.store.dispatch(
      new SearchBill({ billNumber: billNumber.trim(), payerName: payerName.trim(), amount })
    );
  }

  doPayment(dataKey: any): void {
    this.isLoading = true;
    this.paymentError = null;
    this.hidePayment = false;

    this.result$.pipe(takeUntil(this.destroy$), take(1)).subscribe((result) => {
      if (!result) {
        this.paymentError = 'No bill found for payment';
        this.isLoading = false;
        this.hidePayment = true;
        return;
      }

      this.store.dispatch(
        new ProceedCustomPayment({
          dataKey: dataKey.dataKey,
          nameUnderCard: this.nameUnderCard.trim(),
          amount: result.amount.toString(),
          billId: result.billId,
          source: result.source,
          payerId: result.payerId,
        })
      );
    });
  }

  private resetPaymentState(): void {
    this.isLoading = false;
    this.paymentError = null;
    this.paymentSuccess = false;
    this.paymentReceipt = null;
    this.nameUnderCard = '';
    this.invoiceVerified = false;
    this.chargeAuthorized = false;
    this.storeId = '';
    this.hidePayment = false;
    this.canRetry = false;
    this.showPaymentForm = false;
    this.store.dispatch(new ClearPaymentState());
  }

  private resetForm(): void {
    this.invoiceForm.reset();
    this.invoiceForm.markAsUntouched();
    this.invoiceForm.markAsPristine();
  }

  retryPayment(): void {
    const storeId = this.storeId;
    this.resetPaymentState();
    this.storeId = storeId;
    this.showPaymentForm = true;
    this.cdr.markForCheck();
  }

  cancelPayment(): void {
    this.resetForm();
    this.resetPaymentState();
    this.store.dispatch(new ClearPaymentState());
    this.setSelectedIndex(0);
    this.cdr.markForCheck();
  }

  private resolveMonerisStoreId(source: PaymentBillSource): string {
    const cart = environment.shoppingCart;

    switch (source) {
      case PaymentBillSource.Student:
        return cart.studentPaymentMonerisStoreId;
      case PaymentBillSource.LegalEntity:
        return cart.companyMonerisStoreId;
      case PaymentBillSource.Member:
        return cart.memberPaymentMonerisStoreId;
      case PaymentBillSource.DisciplinaryBoard:
        return cart.disciplinaryBoardPaymentMonerisStoreId;
      case PaymentBillSource.FarpOdq:
        return cart.farpOdqBillPaymentMonerisStoreId;
      default:
        return cart.studentPaymentMonerisStoreId;
    }
  }
}
